import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { unit } from '../common/theme';
import { Loader } from '../components/shared/Loader/Loader';
import { useAPI } from '../hooks/useAPI/useAPI';
import { useBranding } from '../hooks/useBranding/useBranding';
import { useCurrentUserContext } from '../hooks/useCurrentUserContext/useCurrentUserContext';
import { useErrorReporting } from '../hooks/useErrorReporting/useErrorReporting';
import { UserContextT } from '../providers/context/CurrentUserContext/CurrentUserContextT';
import useFeatureStore from '../store/featureStore/useFeatureStore';

function RequireAuth({ children }: { children: JSX.Element }) {
  const [currentUserContext, setCurrentUserContext] = useCurrentUserContext();
  const [fetchSessionUserComplete, setFetchSessionUserComplete] =
    useState(false);
  const { branding } = useBranding();
  const { id: authenticatedUserId } = currentUserContext;
  const { addDefaultContextField } = useFeatureStore.getState();
  const { setErrorsUser, setErrorsAdditionalData } = useErrorReporting();
  const api = useAPI();
  const location = useLocation();

  const fetchSessionUserCompleted = (userData?: UserContextT) => {
    // setup user based plugin configurations
    if (userData) {
      addDefaultContextField('userId', userData.id);
      setErrorsUser(userData);
    }

    // setup error reporting branding data
    setErrorsAdditionalData({ brand: branding.brandName });

    setFetchSessionUserComplete(true);
  };

  useFeatureStore((state) => {
    const { lastInteractedFlags } = state;

    // setup error reporting feature flags data
    setErrorsAdditionalData(lastInteractedFlags);
  });

  useEffect(() => {
    if (authenticatedUserId) {
      fetchSessionUserCompleted(currentUserContext);
      return;
    }
    api
      .get('/users/current')
      .then((userData: any) => {
        const currentUser = { ...currentUserContext, ...userData };

        setCurrentUserContext(currentUser);
        fetchSessionUserCompleted(currentUser);
      })
      .catch(() => {
        fetchSessionUserCompleted(currentUserContext);
      });
  }, []);

  if (!fetchSessionUserComplete) {
    return <Loader size={unit[6]} fullScreen />;
  }
  if (!authenticatedUserId) {
    return <Navigate state={{ from: location }} to="./sign-up" replace />;
  }
  return children;
}

export default RequireAuth;
